import type { SortDirection } from "../components/ListTable";
import { useRecordListControlChange } from "./useRecordListControlChange";
import { navigate, useCurrentUrl } from "../lib/urls";
import type { RouteNode } from "../lib/urls/types";

type SortableRoute = RouteNode & { pattern: `${string}sort=:sort${string}` };

export function useUrlSort<C extends string>(
  scope: SortableRoute,
  columns: Record<C, SortDirection>,
  params: { sort: string; dir: string } = { sort: "sort", dir: "dir" }
) {
  const recordControl = useRecordListControlChange();
  const url = useCurrentUrl(scope) as Record<string, string | undefined>;
  const sort = url[params.sort];
  const dir = url[params.dir];
  const sortColumn = sort !== undefined && sort in columns ? (sort as C) : null;
  const sortDirection: SortDirection =
    dir === "asc" || dir === "desc"
      ? dir
      : sortColumn
        ? columns[sortColumn]
        : "desc";
  const setSort = (column: C) => {
    navigate(
      null,
      { [params.sort]: column, [params.dir]: columns[column] },
      { replace: true }
    );
  };
  const handleSort = (column: string) => {
    if (column === sortColumn) {
      const nextDirection = sortDirection === "asc" ? "desc" : "asc";
      recordControl("sort", `${column}:${nextDirection}`);
      navigate(null, { [params.dir]: nextDirection }, { replace: true });
    } else {
      recordControl("sort", `${column}:${columns[column as C]}`);
      setSort(column as C);
    }
  };
  const sortableColumns = Object.keys(columns);
  return { sortColumn, sortDirection, setSort, handleSort, sortableColumns };
}
